import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { copyOrderBlankText, downloadOrderBlankPdf, parseCartUnitPrice } from '../api/orderBlank'
import { saveOrderBlank } from '../api/orderBlankHistory'
import { formatApiErrorMessage } from '../api/apiErrorMessage'
import { useAuth } from '../auth/AuthProvider'
import { standardPlanUiCopy, startPlanUiCopy } from '../billing/startPlanUiCopy'
import { useBuyerEntitlements } from '../hooks/useBuyerEntitlements'
import { usePlanGate } from '../hooks/usePlanGate'
import { buildOrderBlankFilename } from '../utils/orderBlankFilename'
import { formatUnitLabel } from '../utils/formatUnitLabel'
import { cartLineKey } from '../cart/cartLineKey'
import { useCart } from '../cart/CartProvider'
import { groupCartLines } from '../cart/groupCartLines'
import type { CartSupplierGroup } from '../cart/types'
import { MarketEmptyState } from '../components/MarketEmptyState'
import { CartQuantityStepper } from '../components/CartQuantityStepper'
import { MarketPageHeader } from '../components/MarketPageHeader'
import { DataTable } from '../components/DataTable'
import { isPivotModeEnabled } from '../pivot/pivotMode'

const CartLinesTable = ({
  lines,
  onUpdateQuantity,
  onRemoveLine,
}: {
  lines: ReturnType<typeof useCart>['lines']
  onUpdateQuantity: (key: string, quantity: number) => void
  onRemoveLine: (key: string) => void
}) => (
  <DataTable
    wrapClassName="market-table-wrap--cards"
    className="market-table--cards"
    caption="Позиции в заявке"
  >
    <thead>
      <tr>
        <th>Позиция</th>
        <th>Цена</th>
        <th>Ед.</th>
        <th>Поставщик</th>
        <th>Бренд</th>
        <th>Категория</th>
        <th>Кол-во</th>
        <th />
      </tr>
    </thead>
    <tbody>
      {lines.map((line) => {
        const key = cartLineKey(line)
        return (
          <tr key={key}>
            <td className="market-table__cell--primary" data-label="Позиция">
              {line.itemName}
            </td>
            <td className="market-table__cell--price" data-label="Цена">
              {line.price ?? '—'}
            </td>
            <td data-label="Ед.">{formatUnitLabel(line.unit)}</td>
            <td data-label="Поставщик">{line.supplierOrganizationName}</td>
            <td data-label="Бренд">{line.brand ?? '—'}</td>
            <td data-label="Категория">{line.productTypeName}</td>
            <td data-label="Кол-во">
              <CartQuantityStepper
                value={line.quantity}
                onChange={(quantity) => onUpdateQuantity(key, quantity)}
              />
            </td>
            <td className="market-table__cell--action" data-label="">
              <button
                type="button"
                className="echat-btn echat-btn--ghost echat-btn--sm market-table__row-action"
                onClick={() => onRemoveLine(key)}
              >
                Удалить
              </button>
            </td>
          </tr>
        )
      })}
    </tbody>
  </DataTable>
)

const ExternalSupplierActions = ({
  group,
  organizationId,
  restaurantName,
}: {
  group: CartSupplierGroup
  organizationId: number
  restaurantName: string
}) => {
  const [loading, setLoading] = useState<'pdf' | 'text' | 'save' | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  if (group.privateSupplierId == null) {
    return null
  }

  const privateSupplierId = group.privateSupplierId

  const blankRequest = {
    organizationId,
    privateSupplierId,
    lines: group.categories.flatMap((category) =>
      category.lines.map((line) => ({
        catalogItemId: line.catalogItemId,
        quantity: line.quantity,
        unit: formatUnitLabel(line.unit),
        unitPrice: parseCartUnitPrice(line.price),
        ...(line.note.trim() ? { note: line.note.trim() } : {}),
      })),
    ),
  }

  const handleSave = async () => {
    setLoading('save')
    setMessage(null)
    try {
      await saveOrderBlank(blankRequest)
      setMessage('Заявка сохранена в историю — можно открыть снова из вкладки «История»')
    } catch (error) {
      setMessage(formatApiErrorMessage(error, 'Не удалось сохранить заявку'))
    } finally {
      setLoading(null)
    }
  }

  const handlePdf = async () => {
    setLoading('pdf')
    setMessage(null)
    try {
      const blob = await downloadOrderBlankPdf(blankRequest)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = buildOrderBlankFilename(restaurantName, organizationId)
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      setMessage(formatApiErrorMessage(error, 'Не удалось сформировать PDF'))
    } finally {
      setLoading(null)
    }
  }

  const handleCopy = async () => {
    setLoading('text')
    setMessage(null)
    try {
      await copyOrderBlankText(blankRequest)
      setMessage('Текст скопирован в буфер обмена')
    } catch (error) {
      setMessage(formatApiErrorMessage(error, 'Не удалось скопировать текст'))
    } finally {
      setLoading(null)
    }
  }

  return (
    <div className="market-cart-external-actions">
      <div className="market-cart-external-actions__buttons">
        <button
          type="button"
          className="echat-btn echat-btn--primary echat-btn--sm"
          disabled={loading != null}
          onClick={() => void handleSave()}
        >
          {loading === 'save' ? 'Сохраняем…' : 'Сохранить заявку'}
        </button>
        <button
          type="button"
          className="echat-btn echat-btn--ghost echat-btn--sm"
          disabled={loading != null}
          onClick={() => void handlePdf()}
        >
          {loading === 'pdf' ? 'Формируем…' : 'Сформировать бланк заказа'}
        </button>
        <button
          type="button"
          className="echat-btn echat-btn--ghost echat-btn--sm"
          disabled={loading != null}
          onClick={() => void handleCopy()}
        >
          {loading === 'text' ? 'Копируем…' : 'Скопировать текст'}
        </button>
      </div>
      {message ? (
        <p className="market-cart-external-actions__message" role="status">
          {message}
        </p>
      ) : null}
    </div>
  )
}

type CartDraftPanelProps = {
  /** When true, omit outer page section (parent RequestScreen owns chrome). */
  nested?: boolean
}

export const CartDraftPanel = ({ nested = false }: CartDraftPanelProps) => {
  const navigate = useNavigate()
  const pivot = isPivotModeEnabled()
  const { session, organizationName } = useAuth()
  const entitlements = useBuyerEntitlements()
  const uiCopy = entitlements.isStartPlan ? startPlanUiCopy : standardPlanUiCopy
  const { requestFeature, paywallModal } = usePlanGate()
  const { lines, lineCount, updateQuantity, removeLine, clearCart } = useCart()
  const [confirmClear, setConfirmClear] = useState(false)

  const handleCreateRfq = () => {
    if (requestFeature('platform_rfq')) {
      navigate('/orders/create')
    }
  }

  const grouped = useMemo(() => groupCartLines(lines), [lines])
  const registeredLines = useMemo(
    () => grouped.inProvisium.flatMap((group) => group.categories.flatMap((category) => category.lines)),
    [grouped.inProvisium],
  )

  const totalQuantity = useMemo(
    () => lines.reduce((sum, line) => sum + line.quantity, 0),
    [lines],
  )

  const organizationId = session?.organizationId ?? 0
  const hasRegistered = registeredLines.length > 0 && !pivot
  const catalogOrPricesPath = pivot || entitlements.isStartPlan ? '/my-prices' : '/catalog'
  const catalogOrPricesLabel = pivot || entitlements.isStartPlan ? 'В мои прайсы' : 'В каталог'
  const title = pivot ? 'Заявка' : 'Корзина'
  const emptyTitle = pivot ? 'Черновик заявки пуст' : 'Корзина пуста'

  const wrap = (node: ReactNode, withActionBar: boolean) => {
    if (nested) {
      return (
        <div className={withActionBar ? 'market-page--with-action-bar' : undefined}>
          {node}
          {paywallModal}
        </div>
      )
    }
    return (
      <section
        className={
          withActionBar
            ? 'market-page market-page--wide market-page--with-action-bar'
            : 'market-page market-page--wide'
        }
      >
        {node}
        {paywallModal}
      </section>
    )
  }

  if (lineCount === 0) {
    return wrap(
      <>
        {!nested ? (
          <MarketPageHeader
            title={title}
            description={
              pivot || entitlements.isStartPlan
                ? 'Позиции из «Моих прайсов» и бланк заказа для поставщика.'
                : 'Добавьте позиции из каталога поставщиков.'
            }
          />
        ) : (
          <p className="echat-muted market-request-draft-lead">
            Соберите позиции из «Моих прайсов» и сформируйте бланк для поставщика.
          </p>
        )}
        <MarketEmptyState
          title={emptyTitle}
          description={
            pivot
              ? 'Добавьте позиции из «Моих прайсов», чтобы сформировать заявку.'
              : uiCopy.cartEmptyDescription
          }
          actions={[
            {
              label: pivot ? 'Открыть мои прайсы' : uiCopy.cartEmptyActionLabel,
              onClick: () => navigate(catalogOrPricesPath),
            },
          ]}
        />
      </>,
      false,
    )
  }

  return wrap(
    <>
      {!nested ? (
        <MarketPageHeader
          title={title}
          description={`${lineCount} поз. · ${totalQuantity} ед. суммарно`}
          actions={
            <>
              <button
                type="button"
                className="echat-btn echat-btn--ghost"
                onClick={() => navigate(catalogOrPricesPath)}
              >
                {catalogOrPricesLabel}
              </button>
              <button
                type="button"
                className="echat-btn echat-btn--ghost"
                onClick={() => {
                  if (!confirmClear) {
                    setConfirmClear(true)
                    return
                  }
                  clearCart()
                  setConfirmClear(false)
                }}
              >
                {confirmClear ? 'Точно очистить?' : 'Очистить'}
              </button>
              {hasRegistered ? (
                <button type="button" className="echat-btn echat-btn--primary" onClick={handleCreateRfq}>
                  Сформировать заявки
                </button>
              ) : null}
            </>
          }
        />
      ) : (
        <div className="market-request-draft-toolbar">
          <p className="echat-muted">
            {lineCount} поз. · {totalQuantity} ед. суммарно
          </p>
          <div className="market-request-draft-toolbar__actions">
            <button
              type="button"
              className="echat-btn echat-btn--ghost echat-btn--sm"
              onClick={() => navigate(catalogOrPricesPath)}
            >
              {catalogOrPricesLabel}
            </button>
            <button
              type="button"
              className="echat-btn echat-btn--ghost echat-btn--sm"
              onClick={() => {
                if (!confirmClear) {
                  setConfirmClear(true)
                  return
                }
                clearCart()
                setConfirmClear(false)
              }}
            >
              {confirmClear ? 'Точно очистить?' : 'Очистить'}
            </button>
          </div>
        </div>
      )}

      {registeredLines.length > 0 && !pivot ? (
        <>
          <h2 className="market-section-title">В Провизиум</h2>
          <p className="market-muted">{uiCopy.cartRegisteredHint}</p>
          <CartLinesTable
            lines={registeredLines}
            onUpdateQuantity={updateQuantity}
            onRemoveLine={removeLine}
          />
        </>
      ) : null}

      {grouped.external.length > 0 ? (
        <>
          {!pivot ? <h2 className="market-section-title">Вне системы</h2> : null}
          <p className="market-muted">
            Позиции из «Моих прайсов» — сформируйте бланк заказа и отправьте поставщику вручную.
          </p>
          {grouped.external.map((group) => (
            <div
              key={group.privateSupplierId ?? group.supplierOrganizationName}
              className="market-cart-external-group"
            >
              <div className="market-cart-external-group__header">
                <strong>{group.supplierOrganizationName}</strong>
                <ExternalSupplierActions
                  group={group}
                  organizationId={organizationId}
                  restaurantName={organizationName ?? 'restoran'}
                />
              </div>
              <CartLinesTable
                lines={group.categories.flatMap((category) => category.lines)}
                onUpdateQuantity={updateQuantity}
                onRemoveLine={removeLine}
              />
            </div>
          ))}
        </>
      ) : null}

      <div className="market-action-bar" role="region" aria-label="Действия с заявкой">
        <span className="market-action-bar__summary">
          {lineCount} поз. · {totalQuantity} ед.
        </span>
        <div className="market-action-bar__actions">
          <button
            type="button"
            className="echat-btn echat-btn--ghost"
            onClick={() => navigate(catalogOrPricesPath)}
          >
            {catalogOrPricesLabel}
          </button>
          {hasRegistered ? (
            <button type="button" className="echat-btn echat-btn--primary" onClick={handleCreateRfq}>
              Сформировать заявки
            </button>
          ) : null}
        </div>
      </div>
    </>,
    true,
  )
}
